import { marked } from 'marked';

import { formatSessionOffset, preprocessClinicalMarkdown } from './clinicalText';
import type { NdjsonBlock } from './ndjsonBlockTypes';

marked.setOptions({ gfm: true, breaks: false });

function escapeHtml(input: unknown): string {
  // Note: `'` is intentionally not escaped — we only emit attribute values
  // inside double quotes, so `&quot;` is enough to keep them well-formed,
  // and skipping `&#39;` preserves natural apostrophes in body text.
  //
  // `input` is typed `unknown` because the NDJSON grammar's static types
  // (e.g. TableBlock.rows: string[][]) are a lie — the BE has been observed
  // to emit numbers / null inside table cells and evidence items. Coercing
  // here keeps the renderer crash-proof without forcing every call site
  // (table cells, evidence quotes, callout levels) to defend independently.
  let s: string;
  if (typeof input === 'string') s = input;
  else if (input == null) s = '';
  else if (typeof input === 'number' || typeof input === 'boolean') s = String(input);
  // Objects / arrays would stringify to `[object Object]` / `1,2,3` which
  // is rarely what a reader wants — drop to empty so the renderer doesn't
  // smuggle confusing values into the HTML.
  else s = '';
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function renderMarkdown(text: string): string {
  // marked.parse is sync when no async tokenizers are registered (default).
  // preprocessClinicalMarkdown reformats `@ <ms>s` timestamps and wraps
  // quoted speech in <em> before marked renders the line.
  const out = marked.parse(preprocessClinicalMarkdown(text)) as string;
  return out;
}

/**
 * Convert an NDJSON block array into a single HTML string suitable for
 * loading into Tiptap via `useEditor({ content })`. Pure function — no
 * side effects, deterministic, safe to call repeatedly.
 *
 * Control blocks (`meta`, `final_json`, `done`, `error`, `section_end`)
 * produce no output. `section_start` opens a heading; sections are not
 * wrapped in any container element — Tiptap's document model is flat.
 */
export function ndjsonToHtml(blocks: NdjsonBlock[]): string {
  const out: string[] = [];

  for (const block of blocks) {
    switch (block.type) {
      case 'section_start':
        out.push(`<h2>${escapeHtml(block.title)}</h2>`);
        break;

      case 'markdown':
        out.push(renderMarkdown(block.text));
        break;

      case 'table': {
        const rows = block.rows;
        if (rows[0] === undefined) break;
        if (block.title !== undefined && block.title !== '') {
          out.push(`<h3>${escapeHtml(block.title)}</h3>`);
        }
        const [header, ...body] = rows;
        const headerCells = header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join('');
        const bodyRows = body
          .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
          .join('');
        out.push(`<table><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`);
        break;
      }

      case 'callout':
        out.push(
          `<blockquote data-callout="${escapeHtml(block.level)}">${escapeHtml(block.text)}</blockquote>`,
        );
        break;

      case 'evidence': {
        // Quote is wrapped in <em> so the same printed-excerpt CSS rule that
        // styles markdown-rendered evidence-card quotes (via the emphasis
        // wrap in `preprocessClinicalMarkdown`) also lifts the verbatim
        // client speech in NDJSON-rendered evidence blocks.
        const items = block.items
          .map((item) => {
            const meta = `${escapeHtml(item.speaker_label)} · at ${formatSessionOffset(item.start)}`;
            return `<li><strong>${escapeHtml(item.claim)}</strong>: <em>&ldquo;${escapeHtml(item.quote)}&rdquo;</em> <span data-evidence-meta>— ${meta}</span></li>`;
          })
          .join('');
        out.push(`<aside data-evidence><ul>${items}</ul></aside>`);
        break;
      }

      case 'section_end':
      case 'meta':
      case 'final_json':
      case 'done':
      case 'error':
      default:
        break;
    }
  }

  return out.join('\n');
}
